import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconAlertTriangle, IconEye } from "@tabler/icons-react";
import { isRichTextEmpty } from "@congrs/utils";

import PagesEditor from "../Pages/PagesEditor";
import {
  useGetCompliancePolicy,
  useUpdateCompliancePolicy,
} from "../../api/compliance";
import CompliancePreview from "./CompliancePreview";

// The editor works in Tiptap documents; the policy is stored as the serialized
// form of one. A body that predates the editor (or was pasted in as HTML) is
// handed over as-is, which Tiptap parses.
const parsePolicyBody = (body) => {
  if (!body) return null;
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
};

/**
 * The compliance policy is authored once, in the event's main language, and is
 * shown to every attendee exactly as written — there is no per-language
 * authoring and no read-time resolution, so the wording the organizer's legal
 * team signed off on is the wording that is accepted.
 */
export default function PolicyEditor({ eventId, isLive }) {
  const { data: policy, isPending } = useGetCompliancePolicy({ eventId });
  const { mutate: save, isPending: isSaving } = useUpdateCompliancePolicy({
    eventId,
  });

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // The editor reads its content once, at mount, so it is remounted whenever a
  // genuinely different revision arrives. Keying on the saved timestamp rather
  // than on the query result means a background refetch that returns the same
  // revision leaves unsaved edits alone.
  const revision = policy ? `rev-${policy.updatedAt ?? "new"}` : "loading";

  useEffect(() => {
    if (!policy) return;
    setTitle(policy.title ?? "");
    setBody(policy.body ?? "");
    // Re-seeded per revision, not per fetch — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);

  const hasChanged = policy && (policy.title !== title || policy.body !== body);
  const isBodyEmpty = isRichTextEmpty(body);

  const submit = () => {
    setConfirmOpen(false);
    save({ title, body });
  };

  const onSaveClick = () => {
    // Every edit re-prompts everyone, so an organizer fixing a typo on the
    // morning of the event is told what it will cost before it happens.
    if (hasChanged && policy?.responseCount > 0) {
      setConfirmOpen(true);
      return;
    }
    submit();
  };

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={4}>Compliance text</Title>
            <Text size="sm" c="dimmed">
              Written once in the event&apos;s main language
              {policy?.languageCode ? ` (${policy.languageCode})` : ""}, and
              shown to every attendee exactly as written.
            </Text>
          </div>
          <Button
            variant="default"
            leftSection={<IconEye size={16} />}
            onClick={() => setPreviewOpen(true)}
          >
            Preview
          </Button>
        </Group>

        {!isLive && (
          <Alert color="blue" variant="light">
            Nobody is being gated. The gate goes live once this text has content
            and the compliance feature is on under Event Settings → Features.
          </Alert>
        )}

        {isLive && (
          <Alert color="orange" variant="light" icon={<IconAlertTriangle />}>
            The compliance gate is live. Editing this text clears every answer
            collected so far and asks all attendees again the next time they
            enter the event.
          </Alert>
        )}

        <TextInput
          label="Screen title"
          description="What attendees see at the top of the screen, e.g. Code of Conduct."
          value={title}
          onChange={(event) => setTitle(event.currentTarget.value)}
          maxLength={255}
        />

        <PagesEditor
          key={revision}
          content={parsePolicyBody(policy?.body)}
          onChange={setBody}
          withImage={false}
        />

        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {policy?.responseCount
              ? `${policy.responseCount} answer${
                  policy.responseCount === 1 ? "" : "s"
                } recorded against the current text`
              : "No answers recorded yet"}
          </Text>
          <Button
            onClick={onSaveClick}
            loading={isSaving}
            disabled={isPending || isBodyEmpty}
          >
            Save
          </Button>
        </Group>

        {isBodyEmpty && (
          <Text size="xs" c="dimmed">
            The compliance feature cannot be turned on while this text is empty.
          </Text>
        )}
      </Stack>

      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="This will clear every answer"
        centered
      >
        <Stack>
          <Text size="sm">
            {policy?.responseCount} attendee
            {policy?.responseCount === 1 ? " has" : "s have"} already answered
            these terms. Saving this change clears every answer, and all of them
            will be asked again the next time they enter the event.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={submit}>
              Save and clear {policy?.responseCount} answers
            </Button>
          </Group>
        </Stack>
      </Modal>

      <CompliancePreview
        opened={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={title}
        body={body}
        languageCode={policy?.languageCode}
      />
    </Card>
  );
}
